import React, { useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';

import { login } from '../../services/api/users';
import { useAuth } from '../../hooks/use-auth';
import { strings, parameters } from '../../localization/strings';
import CommonAnimatedLoader from '../common/animated/loader';
import StyledLabel from '../common/form/styled-label';
import StyledInput from '../common/form/styled-input';
import StyledError from '../common/form/styled-error';
import CommonButtonDefault from '../common/buttons/default';
import { getLocalizedPath } from '../../helpers/localization-helper';
import { useSite } from '../../hooks/use-site';

interface ComponentProps {
  orderHash?: string;
  prefillEmail?: string;
  isQuestionnaire?: boolean;
}

type Inputs = {
  common: string;
  email: string;
  password: string;
};

function UsersLoginForm({ orderHash: orderHashProp, prefillEmail, isQuestionnaire }: ComponentProps) {
  const { setUserToken } = useAuth();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { locale, isSelfCo, region } = useSite();

  // Get orderHash from prop, URL params, or sessionStorage (Shopify flow)
  const shopifyOrder = sessionStorage.getItem('shopify_order');
  const shopifyOrderData = shopifyOrder ? JSON.parse(shopifyOrder) : null;
  const orderHash = orderHashProp || searchParams.get('orderHash') || shopifyOrderData?.orderHash || undefined;

  const {
    register,
    handleSubmit,
    setError,
    setValue,
    clearErrors,
    formState: { errors },
  } = useForm<Inputs>();
  const onSubmit: SubmitHandler<Inputs> = async (data) => {
    try {
      setIsLoading(true);
      const token = await login(data.email, data.password, orderHash);

      await setUserToken(token, false);

      // Check if there's a returnUrl to redirect to
      const returnUrl = searchParams.get('returnUrl');

      if (returnUrl) {
        navigate(returnUrl);
      } else if (!isQuestionnaire) {
        navigate(getLocalizedPath(parameters().firstPageUrl, locale, region));
      } else if (orderHash && (token.orderStatus === 'paid' || token.orderStatus === 'completed')) {
        // Shopify order already paid - go to dashboard
        navigate(getLocalizedPath(parameters().dashboard, locale, region));
      } else if (isSelfCo) {
        navigate(getLocalizedPath(parameters().shipment, locale, region));
      } else {
        navigate(getLocalizedPath(parameters().selectPatient, locale, region));
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsLoading(false);
        setError('common', { message: e.message });
      }
    }
  };

  useEffect(() => {
    const emailToFill = prefillEmail || searchParams.get('email');

    if (emailToFill) {
      setValue('email', emailToFill);
    }
  }, [prefillEmail, searchParams, setValue]);

  return (
    <>
      {isLoading && <CommonAnimatedLoader />}
      {errors.common && <StyledError>{errors.common.message}</StyledError>}
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="py-2">
          <StyledLabel htmlFor="email">{strings().form.email}</StyledLabel>
          <StyledInput
            fullWidth
            className={` ${errors.email && `!border-red-500`} `}
            type="email"
            {...register('email', { required: true })}
          />
          {errors.email && <StyledError>{strings().error.fieldIsRequired}</StyledError>}
        </div>
        <div className="py-2">
          <StyledLabel htmlFor="password">{strings().form.password}</StyledLabel>
          <StyledInput
            fullWidth
            type="password"
            className={` ${errors.password && `!border-red-500`} `}
            {...register('password', { required: true })}
          />
          {errors.password && <StyledError>{strings().error.fieldIsRequired}</StyledError>}
        </div>
        <div className="text-sm">
          <Link to={getLocalizedPath('/forgot-password', locale, region)} className="font-medium">
            {strings().login.forgotYourPasswordLink}
          </Link>
        </div>
        <div className="mt-5 md:mt-7 flex justify-end">
          <CommonButtonDefault
            primary
            type="submit"
            onClick={() => {
              clearErrors('common');
            }}
          >
            {strings().button.login}
          </CommonButtonDefault>
        </div>
      </form>
    </>
  );
}

UsersLoginForm.defaultProps = {
  orderHash: '',
  prefillEmail: '',
  isQuestionnaire: false,
};

export default UsersLoginForm;
